import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { CrmTask } from "../../../models/Task";
import { crm } from "./crmTheme";

type ViewMode = "month" | "week";

const DOW_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const TaskChip = ({ task, onClick }: { task: CrmTask; onClick: () => void }) => (
  <Tooltip
    label={`${task.title}${task.responsibleName ? ` · ${task.responsibleName}` : ""}${task.taskStatusName ? ` [${task.taskStatusName}]` : ""}`}
    placement="top"
    hasArrow
  >
    <Box
      bg="#f0fdf4"
      border="1px solid"
      borderColor="#bbf7d0"
      borderLeft={`3px solid ${crm.success}`}
      borderRadius="4px"
      px="4px"
      py="1px"
      fontSize="10px"
      cursor="pointer"
      color={crm.success}
      _hover={{ bg: "#dcfce7" }}
      onClick={onClick}
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      w="100%"
    >
      {task.title}
    </Box>
  </Tooltip>
);

const TaskCalendar = ({
  tasks,
  onTaskClick,
}: {
  tasks: CrmTask[];
  onTaskClick: (t: CrmTask) => void;
}) => {
  const [anchorDate, setAnchorDate] = useState(DateTime.now().startOf("day"));
  const [viewMode, setViewMode] = useState<ViewMode>("month");

  const today = DateTime.now().toISODate();

  const tasksByDay = useMemo(() => {
    const map: Record<string, CrmTask[]> = {};
    for (const t of tasks) {
      if (!t.dueDate) continue;
      const key = DateTime.fromISO(t.dueDate).toISODate()!;
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return map;
  }, [tasks]);

  const currentMonth = anchorDate.startOf("month");

  const weeks = useMemo(() => {
    const start = currentMonth;
    const daysInMonth = currentMonth.daysInMonth!;
    const offset = start.weekday % 7;
    const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
    const cells = Array.from({ length: totalCells }, (_, i) => {
      const dayNum = i - offset + 1;
      return dayNum >= 1 && dayNum <= daysInMonth ? start.set({ day: dayNum }) : null;
    });
    const result: (DateTime | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) result.push(cells.slice(i, i + 7));
    return result;
  }, [currentMonth]);

  const weekStart = useMemo(
    () => anchorDate.minus({ days: anchorDate.weekday % 7 }),
    [anchorDate]
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i })),
    [weekStart]
  );

  const goBack = () =>
    setAnchorDate((d) =>
      viewMode === "month" ? d.minus({ months: 1 }) : d.minus({ weeks: 1 })
    );
  const goForward = () =>
    setAnchorDate((d) =>
      viewMode === "month" ? d.plus({ months: 1 }) : d.plus({ weeks: 1 })
    );
  const goToday = () => setAnchorDate(DateTime.now().startOf("day"));

  const headerLabel =
    viewMode === "month"
      ? currentMonth.setLocale("pt-BR").toFormat("MMMM yyyy")
      : (() => {
          const weekEnd = weekStart.plus({ days: 6 });
          const sameMonth = weekStart.month === weekEnd.month;
          const sameYear = weekStart.year === weekEnd.year;
          const startFmt = sameMonth
            ? weekStart.toFormat("d")
            : sameYear
            ? weekStart.setLocale("pt-BR").toFormat("d MMM")
            : weekStart.setLocale("pt-BR").toFormat("d MMM yyyy");
          return `${startFmt} — ${weekEnd.setLocale("pt-BR").toFormat("d MMM yyyy")}`;
        })();

  return (
    <Box overflowY="auto" maxH="calc(100vh - 260px)">
      <Flex align="center" justify="space-between" mb="16px" flexWrap="wrap" gap="8px">
        <Flex align="center" gap="8px">
          <IconButton
            aria-label="Período anterior"
            icon={<FaChevronLeft />}
            size="sm"
            variant="ghost"
            color={crm.textMuted}
            _hover={{ color: crm.text, bg: crm.subtle }}
            onClick={goBack}
          />
          <Text
            fontWeight="600"
            textTransform="capitalize"
            fontSize="md"
            minW="180px"
            textAlign="center"
            color={crm.text}
          >
            {headerLabel}
          </Text>
          <IconButton
            aria-label="Próximo período"
            icon={<FaChevronRight />}
            size="sm"
            variant="ghost"
            color={crm.textMuted}
            _hover={{ color: crm.text, bg: crm.subtle }}
            onClick={goForward}
          />
          <Button
            size="sm"
            variant="outline"
            borderColor={crm.border}
            color={crm.text}
            _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
            borderRadius="8px"
            onClick={goToday}
          >
            Hoje
          </Button>
        </Flex>

        {/* Segmented view toggle */}
        <Flex
          bg={crm.subtle}
          p="4px"
          borderRadius="10px"
          border={`1px solid ${crm.border}`}
          gap="2px"
        >
          {(["month", "week"] as ViewMode[]).map((mode) => (
            <Button
              key={mode}
              size="sm"
              borderRadius="8px"
              px="16px"
              py="6px"
              fontSize="sm"
              fontWeight={viewMode === mode ? "600" : "400"}
              color={viewMode === mode ? crm.brand : crm.textMuted}
              bg={viewMode === mode ? crm.surface : "transparent"}
              boxShadow={viewMode === mode ? crm.shadowSm : "none"}
              _hover={{ color: viewMode === mode ? crm.brand : crm.text }}
              transition="all .15s"
              onClick={() => setViewMode(mode)}
            >
              {mode === "month" ? "Mês" : "Semana"}
            </Button>
          ))}
        </Flex>
      </Flex>

      {/* Month view */}
      {viewMode === "month" && (
        <Box
          border={`1px solid ${crm.border}`}
          borderRadius="14px"
          overflow="hidden"
          boxShadow={crm.shadowSm}
        >
          <Flex>
            {DOW_LABELS.map((d) => (
              <Box
                key={d}
                flex={1}
                py="8px"
                textAlign="center"
                fontSize="xs"
                fontWeight="700"
                color={crm.textMuted}
                borderBottom={`1px solid ${crm.border}`}
                bg={crm.subtle}
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                {d}
              </Box>
            ))}
          </Flex>
          {weeks.map((week, wi) => (
            <Flex
              key={wi}
              borderBottom={wi < weeks.length - 1 ? `1px solid ${crm.border}` : undefined}
            >
              {week.map((day, di) => {
                const isoDate = day?.toISODate() ?? null;
                const dayTasks = isoDate ? (tasksByDay[isoDate] ?? []) : [];
                const isToday = isoDate === today;
                return (
                  <Box
                    key={di}
                    flex={1}
                    minH="80px"
                    p="4px"
                    borderLeft={di > 0 ? `1px solid ${crm.border}` : undefined}
                    bg={!day ? crm.subtle : undefined}
                  >
                    {day && (
                      <>
                        <Flex
                          w="24px"
                          h="24px"
                          align="center"
                          justify="center"
                          borderRadius="full"
                          bg={isToday ? crm.brand : undefined}
                          color={isToday ? "white" : crm.textMuted}
                          fontSize="xs"
                          fontWeight={isToday ? "700" : "400"}
                          mb="4px"
                        >
                          {day.day}
                        </Flex>
                        <Flex flexDir="column" gap="2px">
                          {dayTasks.map((t) => (
                            <TaskChip key={t.id} task={t} onClick={() => onTaskClick(t)} />
                          ))}
                        </Flex>
                      </>
                    )}
                  </Box>
                );
              })}
            </Flex>
          ))}
        </Box>
      )}

      {/* Week view — chips only, no time grid */}
      {viewMode === "week" && (
        <Box
          border={`1px solid ${crm.border}`}
          borderRadius="14px"
          overflow="hidden"
          boxShadow={crm.shadowSm}
        >
          <Flex>
            {weekDays.map((day, di) => {
              const isoDate = day.toISODate()!;
              const isToday = isoDate === today;
              const dayTasks = tasksByDay[isoDate] ?? [];
              return (
                <Flex
                  key={di}
                  flex={1}
                  flexDir="column"
                  borderLeft={di > 0 ? `1px solid ${crm.border}` : undefined}
                  bg={isToday ? "#f0fdf4" : undefined}
                >
                  {/* Day header */}
                  <Flex
                    flexDir="column"
                    align="center"
                    py="8px"
                    gap="2px"
                    borderBottom={`1px solid ${crm.border}`}
                    bg={isToday ? "#f0fdf4" : crm.subtle}
                  >
                    <Text
                      fontSize="10px"
                      fontWeight="700"
                      color={isToday ? crm.success : crm.textMuted}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      {DOW_LABELS[di]}
                    </Text>
                    <Flex
                      w="28px"
                      h="28px"
                      align="center"
                      justify="center"
                      borderRadius="full"
                      bg={isToday ? crm.brand : undefined}
                      color={isToday ? "white" : crm.textMuted}
                      fontWeight={isToday ? "700" : "400"}
                      fontSize="sm"
                    >
                      {day.day}
                    </Flex>
                  </Flex>

                  {/* Task chips */}
                  <Flex flexDir="column" gap="4px" p="6px" minH="120px">
                    {dayTasks.map((t) => (
                      <TaskChip key={t.id} task={t} onClick={() => onTaskClick(t)} />
                    ))}
                    {dayTasks.length === 0 && (
                      <Text fontSize="10px" color={crm.textFaint} textAlign="center" mt="8px">
                        —
                      </Text>
                    )}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default TaskCalendar;
